import React, { useMemo } from 'react';
import { Icon, Button } from '../../campaign-os';

/**
 * The last screen before a campaign goes out, and the only place a bad address can still
 * be caught.
 *
 * Two kinds of trouble, deliberately separated because they call for different actions.
 * An address the provider will refuse never reaches anyone — the vendor record has to be
 * corrected before that company hears from us at all. An address that already bounced is
 * the expensive one: mailing dead mailboxes repeatedly is read by receiving domains as a
 * spam signal, and the cost falls on every later send from the same account, transactional
 * mail included.
 *
 * It never blocks the launch. The operator may have a reason, and a check that refuses to
 * let you work gets worked around.
 */
const num = (n) => Number(n || 0).toLocaleString('en-US');

// Everything but a one-off soft bounce is skipped by the send itself (recorded as "not
// sent" with the reason), so these say what WILL happen, and what fixes it.
const REASONS = {
  unsendable: {
    label: 'Skipped: malformed address',
    detail: 'No real address can be read from the field (a space inside it, a missing @, a website). Fix the vendor record to reach them.',
    tone: 'coral',
    icon: 'error',
  },
  placeholder: {
    label: 'Skipped: no real email on the record',
    detail: 'A placeholder like no-email-…@placeholder.com, or "not available". Add the vendor’s real address.',
    tone: 'coral',
    icon: 'person_off',
  },
  misspelt_domain: {
    label: 'Skipped: misspelt domain',
    detail: 'gamil.com, gmial.com, gmail.co… Correct the domain on the vendor record.',
    tone: 'coral',
    icon: 'spellcheck',
  },
  no_mail_domain: {
    label: 'Skipped: the domain does not exist',
    detail: 'The part after @ no longer exists, so every attempt would bounce. Ask the vendor for a current address.',
    tone: 'coral',
    icon: 'cloud_off',
  },
  unsubscribed: {
    label: 'Skipped: unsubscribed',
    detail: 'The recipient asked not to be mailed. They are never mailed again.',
    tone: 'coral',
    icon: 'unsubscribe',
  },
  bounced: {
    label: 'Skipped: bounced before',
    detail: 'A previous send was refused for good (the mailbox does not exist). Mailing it again would only damage sending reputation.',
    tone: 'coral',
    icon: 'undo',
  },
  soft_bounced: {
    label: 'Temporarily failed before, once — will be sent',
    detail: 'A mailbox was full or a server was busy, one time. Worth another try — a repeat moves it to “bounced before”.',
    tone: 'amber',
    icon: 'schedule',
  },
};

const ORDER = ['unsendable', 'placeholder', 'misspelt_domain', 'no_mail_domain', 'unsubscribed', 'bounced', 'soft_bounced'];

/** What the send will actually do, when the audience is per vendor. */
function PlanNote({ plan }) {
  if (!plan) return null;
  return (
    <p className="mt-3 flex max-w-[560px] items-start gap-1.5 text-left text-2xs leading-[16px] text-cos-ink-secondary">
      <Icon name="send" className="mt-px shrink-0 text-[14px] text-cos-teal-deep" />
      <span>
        <span className="mono font-bold text-cos-ink">{num(plan.addresses)}</span> mail{plan.addresses === 1 ? '' : 's'} to{' '}
        <span className="mono font-bold text-cos-ink">{num(plan.vendors_reached)}</span> of {num(plan.vendors)} vendors.
        {plan.split_vendors > 0 &&
          ` ${num(plan.split_vendors)} vendor${plan.split_vendors === 1 ? ' has' : 's have'} several addresses in one field — each gets its own mail.`}
        {plan.copies_saved > 0 &&
          ` ${num(plan.shared_addresses)} address${plan.shared_addresses === 1 ? ' is' : 'es are'} on more than one vendor record and get${plan.shared_addresses === 1 ? 's' : ''} one copy each — ${num(plan.copies_saved)} fewer repeat mail${plan.copies_saved === 1 ? '' : 's'}.`}
        {!plan.copies_saved && plan.shared_addresses > 0 &&
          ` ${num(plan.shared_addresses)} address${plan.shared_addresses === 1 ? ' is' : 'es are'} on more than one vendor record and will get the mail once per vendor.`}
      </span>
    </p>
  );
}

/**
 * What the send costs against the provider's hourly cap.
 *
 * Two very different messages share this slot. A per-vendor send that is bigger than the
 * hour is a PACE: the queue releases it at the cap and nothing is lost, so the operator
 * only needs to know it will take a while. A shared send whose single request is bigger
 * than the hour is a REFUSAL: waiting cannot help, the provider would block the rest and
 * lose it, and the launch button is disabled until the list is split.
 */
function HourlyNote({ hourly }) {
  if (!hourly || !hourly.limit) return null;
  const { limit, per_request, fits_in_one_request, total_recipients, estimated_hours, used_last_hour } = hourly;
  const left = Math.max(0, limit - (used_last_hour || 0));

  if (!fits_in_one_request) {
    return (
      <div className="mt-3 w-full max-w-[560px] rounded-xl border border-cos-status-coral/30 bg-cos-status-coral-bg/40 px-4 py-3 text-left">
        <div className="flex items-center gap-2">
          <Icon name="block" filled className="shrink-0 text-[16px] text-cos-status-coral" />
          <span className="text-[13px] font-semibold text-cos-ink">This mail cannot be sent as one message</span>
        </div>
        <p className="mt-1 text-[11px] leading-[16px] text-cos-ink-secondary">
          It carries <span className="mono font-bold text-cos-ink">{num(per_request)}</span> addresses (the
          recipient plus CC plus BCC), and this account sends at most{' '}
          <span className="mono font-bold text-cos-ink">{num(limit)}</span> an hour. The provider would deliver
          the first {num(limit)} and <strong>block the other {num(per_request - limit)} for good</strong> — they
          are not queued or retried. Split the copy list into campaigns of at most {num(limit - 1)}, or raise the
          hourly limit with the provider first.
        </p>
      </div>
    );
  }

  if (estimated_hours > 1) {
    return (
      <p className="mt-3 flex max-w-[560px] items-start gap-1.5 text-left text-2xs leading-[16px] text-cos-ink-secondary">
        <Icon name="schedule" className="mt-px shrink-0 text-[14px] text-cos-ink-muted" />
        <span>
          <span className="mono font-bold text-cos-ink">{num(total_recipients)}</span> addresses at up to{' '}
          <span className="mono font-bold text-cos-ink">{num(limit)}</span> an hour — about{' '}
          <span className="mono font-bold text-cos-ink">{num(estimated_hours)}</span> hours to finish. Nothing
          is lost: the queue paces it and the campaign shows progress as it goes.
          {used_last_hour > 0 ? ` ${num(left)} of this hour's allowance is still free.` : ''}
        </span>
      </p>
    );
  }

  if (used_last_hour > 0 && total_recipients > left) {
    return (
      <p className="mt-3 flex max-w-[560px] items-start gap-1.5 text-left text-2xs leading-[16px] text-cos-ink-secondary">
        <Icon name="schedule" className="mt-px shrink-0 text-[14px] text-cos-ink-muted" />
        <span>
          Only <span className="mono font-bold text-cos-ink">{num(left)}</span> of this hour's{' '}
          {num(limit)} are still free, so part of this send will wait for the next hour. Nothing is lost.
        </span>
      </p>
    );
  }

  return null;
}

export function PreSendCheck({ result, loading, onDropFromCopyLists }) {
  const groups = useMemo(() => {
    const problems = result?.problems ?? [];
    return ORDER.map((reason) => ({ reason, rows: problems.filter((p) => p.reason === reason) })).filter(
      (g) => g.rows.length > 0
    );
  }, [result]);

  if (loading) {
    return (
      <p className="mt-4 flex items-center gap-2 text-2xs text-cos-ink-muted">
        <Icon name="progress_activity" className="animate-spin text-[14px]" />
        Checking the list for addresses that will fail…
      </p>
    );
  }

  if (!result) return null;

  // A clean list says so once, quietly, and gets out of the way.
  if (groups.length === 0) {
    return (
      <div className="flex w-full flex-col items-center">
        <p className="mt-4 flex items-center gap-1.5 text-2xs text-cos-teal-deep">
          <Icon name="check_circle" filled className="text-[14px]" />
          All {num(result.checked)} addresses look sendable, and none of them has bounced before.
        </p>
        <PlanNote plan={result.plan} />
        <HourlyNote hourly={result.hourly} />
      </div>
    );
  }

  // Only the hopeless ones are worth offering to remove: a re-send cannot rescue either.
  const removable = groups
    .filter((g) => g.reason !== 'soft_bounced')
    .flatMap((g) => g.rows.map((r) => r.email));

  return (
    <div className="flex w-full flex-col items-center">
      <div className="mt-4 w-full max-w-[560px] overflow-hidden rounded-xl border border-cos-status-amber/30 bg-cos-status-amber-bg/40 text-left">
      <div className="flex items-center gap-2 border-b border-cos-status-amber/20 px-4 py-2.5">
        <Icon name="warning" filled className="shrink-0 text-[16px] text-cos-status-amber" />
        <span className="text-[13px] font-semibold text-cos-ink">
          {num(result.problems.length)} of {num(result.checked)} addresses need a look
        </span>
        <span className="flex-1" />
        <span className="mono text-2xs text-cos-ink-secondary">{num(result.reachable)} reachable</span>
      </div>

      <div className="max-h-[220px] overflow-y-auto px-4 py-3">
        {groups.map((g) => {
          const meta = REASONS[g.reason];
          return (
            <div key={g.reason} className="mb-3 last:mb-0">
              <div className="flex items-center gap-1.5">
                <Icon
                  name={meta.icon}
                  className={`text-[14px] ${meta.tone === 'coral' ? 'text-cos-status-coral' : 'text-cos-status-amber'}`}
                />
                <span className="text-2xs font-semibold text-cos-ink">
                  {meta.label} · {num(g.rows.length)}
                </span>
              </div>
              <p className="mb-1.5 ml-[22px] text-[11px] leading-[15px] text-cos-ink-secondary">{meta.detail}</p>
              <ul className="ml-[22px]">
                {g.rows.slice(0, 6).map((r) => (
                  <li key={`${r.reason}-${r.email}`} className="flex items-baseline gap-2 py-0.5">
                    <span className="mono truncate text-[11px] text-cos-ink">{r.email}</span>
                    {r.vendor_code && (
                      <span className="mono shrink-0 text-[10px] text-cos-ink-muted">{r.vendor_code}</span>
                    )}
                    {r.company_name && (
                      <span className="truncate text-[11px] text-cos-ink-secondary">{r.company_name}</span>
                    )}
                  </li>
                ))}
                {g.rows.length > 6 && (
                  <li className="py-0.5 text-[11px] text-cos-ink-muted">and {num(g.rows.length - 6)} more</li>
                )}
              </ul>
            </div>
          );
        })}
      </div>

      {removable.length > 0 && typeof onDropFromCopyLists === 'function' && (
        <div className="flex items-center gap-2 border-t border-cos-status-amber/20 px-4 py-2.5">
          <span className="text-[11px] leading-[15px] text-cos-ink-secondary">
            You can send anyway — nothing here blocks the launch.
          </span>
          <span className="flex-1" />
          <Button size="sm" variant="ghost" icon="playlist_remove" onClick={() => onDropFromCopyLists(removable)}>
            Remove {num(removable.length)} from CC/BCC
          </Button>
        </div>
      )}
      </div>
      <PlanNote plan={result.plan} />
      <HourlyNote hourly={result.hourly} />
    </div>
  );
}

export default PreSendCheck;
